import dayjs from 'dayjs';
import download from 'downloadjs';
import { toJpeg } from 'html-to-image';
import { cloneDeep, get, isEmpty, isEqual, unset as _unset, xorWith } from 'lodash';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { FieldPath, FieldValues } from 'react-hook-form';

import { ProductLine } from '@/app/factory-database/src/typings/product-line';
import { ActivityUnit } from '@/app/org-cfv/src/enums/ActivityDataTypeEnum';
import {
  EmissionSourceCategory,
  EmissionSourceFactorUnitType,
  EmissionSourceSubCategory,
} from '@/app/org-cfv/src/enums/EmissionSourceEnum';
import { getUnitTypeByActivityUnit } from '@/app/org-cfv/utils';
import { BE_LANG, FE_LANG } from '@/constants/common';
import { APP_DATE_FORMAT } from '@/constants/date';
import { NextTFunction } from '@/i18n/client';
import { languages } from '@/i18n/settings';
import { Meta } from '@/typings/common';
import yup from '@/utils/yup';

export const isEnabledReduxDevTool = process.env.NEXT_PUBLIC_DEV_TOOLS === 'enabled';

export const checkIfUnitChangedValid = (originalUnit?: ActivityUnit, newUnit?: ActivityUnit) => {
  if (!originalUnit) {
    return true;
  }

  const originalUnitType = getUnitTypeByActivityUnit(originalUnit);
  const newUnitType = getUnitTypeByActivityUnit(newUnit);

  if (originalUnitType !== newUnitType) {
    return false;
  }
  if (originalUnitType === EmissionSourceFactorUnitType.OTHER && originalUnit !== newUnit) {
    return false;
  }

  return true;
};

export const unset = <T extends object>(source: T, key: FieldPath<T>): T => {
  const clone = cloneDeep(source);
  _unset(clone, key);
  const keys = key.split('.');
  for (let i = keys.length - 1; i > 0; i--) {
    const _key = keys.slice(0, i).join('.') as FieldPath<T>;
    const value = get<T, FieldPath<T>>(clone, _key);
    if (value && typeof value === 'object' && Object.keys(value).length === 0) {
      _unset(clone, _key);
    }
  }
  return clone;
};

export const convertNumberInput = <T>(value: T) => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const number = Number(value);
  if (isNaN(number)) {
    return value;
  }

  return number;
};

export const isServer = typeof window === 'undefined';

export const getFieldPathCodeWithinMigratedData = <T extends FieldValues>(
  migratedData: T,
  parentPath?: FieldPath<T>,
): { path: FieldPath<T>; code: number }[] => {
  let paths: { path: FieldPath<T>; code: number }[] = [];

  Object.keys(migratedData).forEach((key) => {
    const path = key as FieldPath<T>;
    const fullPath = (parentPath ? `${parentPath}.${path}` : path) as FieldPath<T>;
    if (migratedData[path]?.code) {
      paths.push({ path: fullPath, code: migratedData[path].code });
    } else if (Array.isArray(migratedData[path]) && migratedData[path].length > 0) {
      for (let i = 0; i < migratedData[path].length; i++) {
        paths = paths.concat(
          getFieldPathCodeWithinMigratedData(migratedData[path][i], `${fullPath}.[${i}]`) as {
            path: FieldPath<T>;
            code: number;
          }[],
        );
      }
    } else if (typeof migratedData === 'object' && !isEmpty(migratedData[path])) {
      paths = paths.concat(
        getFieldPathCodeWithinMigratedData(migratedData[path] as FieldValues, fullPath) as {
          path: FieldPath<T>;
          code: number;
        }[],
      );
    }
  });

  return paths;
};

// compare pathname and sidebar url
export const comparePathnameAndSidebarItemUrl = ({
  pathname = '',
  url = '',
  isStartsWith = true,
}: {
  pathname: string;
  url?: string;
  isStartsWith?: boolean;
}) => {
  const pathnameFirstSegment = pathname.split('/')[1] || '';
  const urlFirstSegment = url.split('/')[1] || '';

  const pathnameLng = languages.find((l) => l.toLowerCase() === pathnameFirstSegment.toLowerCase()) || '';
  const urlLng = languages.find((l) => l.toLowerCase() === urlFirstSegment.toLowerCase()) || '';

  const pathnameWithoutLng = pathnameLng ? pathname.replace(`/${pathnameLng}`, '') : pathname;
  const urlWithoutLng = urlLng ? url.replace(`/${urlLng}`, '') : url;

  const pathnameWithoutQuery = pathnameWithoutLng.split('?')[0];
  const urlWithoutQuery = urlWithoutLng.split('?')[0];
  if (!pathnameWithoutQuery && !urlWithoutQuery) {
    return false;
  }
  return isStartsWith ? pathnameWithoutQuery.startsWith(urlWithoutQuery) : pathnameWithoutQuery === urlWithoutQuery;
};

export const transferFeLangToBeLang = (lng: FE_LANG) => lng.toLowerCase().replace('-', '_') as BE_LANG;

export const getGeographyAreaName = (
  area: {
    key: string;
    en_us?: string;
    zh_tw?: string;
    zh_cn?: string;
  },
  lng: FE_LANG,
) => {
  if (!area) {
    return '';
  }

  const areaName = area[transferFeLangToBeLang(lng)] || area.en_us || '';
  if (!areaName) {
    try {
      const keys = JSON.parse(area.key);
      return keys[0];
    } catch (error) {
      return '';
    }
  }
  return '';
};

export const fetchAllPages = async <T extends { query?: { page?: number; limit?: number } }, S>({
  trigger,
  params,
  listKey = 'list',
}: {
  trigger: (params: T) => Promise<{ data: { list?: S[]; data?: S[]; meta: Meta } }>;
  params: T;
  listKey?: 'list' | 'data';
}): Promise<S[]> => {
  let page = 1;
  let hasNextPage = true;
  let data: S[] = [];
  while (hasNextPage) {
    const response = await trigger({ query: { ...params.query, page } } as T);
    const list = response?.data?.[listKey] || [];
    const meta = response?.data?.meta || { hasNextPage: false };

    if (list.length > 0) {
      data = data.concat(list);
    }
    hasNextPage = meta.hasNextPage;
    page++;
  }
  return data;
};

export const fetchAllPagesParallel = async <T extends { query?: { page?: number; limit?: number } }, S>({
  trigger,
  params,
  callbackMeta,
  callbackData,
  parallelFetchNumber = 10,
}: {
  trigger: (params: T) => Promise<{ data: { data?: S[]; list?: S[]; meta: Meta } }>;
  params: T;
  callbackMeta?: (meta: Meta) => void;
  callbackData?: (data: S[]) => void;
  parallelFetchNumber?: number;
}) => {
  const baseQuery = cloneDeep({ ...params.query, limit: params.query?.limit || 50 });
  let data: S[] = [];
  let page = 1;
  const page1Res = await trigger({ query: { ...baseQuery, page } } as T).catch(() => null);
  if (!page1Res) {
    return [];
  }
  const list = page1Res.data.list || page1Res.data.data || [];
  if (callbackMeta) {
    callbackMeta(page1Res.data.meta);
  }
  if (callbackData && list.length > 0) {
    callbackData(list);
  }

  if (page1Res) {
    data = data.concat(list);
    while (page < page1Res.data.meta.pageCount) {
      const parallelFetchList = [];
      for (let i = 1; i <= parallelFetchNumber; i++) {
        if (page + i <= page1Res.data.meta.pageCount) {
          parallelFetchList.push(trigger({ query: { ...baseQuery, page: page + i } } as T).catch(() => null));
        }
      }
      const resultList = (await Promise.all(parallelFetchList)).filter((r) => !isEmpty(r));
      const lastMeta = resultList[resultList.length - 1]?.data.meta;
      if (callbackMeta && lastMeta) {
        callbackMeta(lastMeta);
      }
      const partialData = resultList?.map((r) => r?.data.list || r?.data.data || []).flat() || [];
      if (callbackData && partialData.length > 0) {
        callbackData(partialData);
      }
      data = data.concat(partialData);
      page += parallelFetchNumber;
    }
  }
  return data;
};

export const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const getProductLineFunctionalDeclaredUnit = (productLine?: ProductLine | null): string => {
  if (!productLine) {
    return '';
  }
  const { amount, unit, measurementAmount, measurementUnit } = productLine;
  const count = `${amount} ${unit}`;
  const perUnitCount = measurementAmount && measurementUnit ? `${measurementAmount} ${measurementUnit}` : '';

  return `${count}${perUnitCount ? ` ${perUnitCount}` : ''}`;
};

export const formatDate = (date?: string | Date | null, format = APP_DATE_FORMAT): string => {
  if (!date) {
    return '';
  }
  const formattedDate = dayjs(date);
  if (!formattedDate.isValid()) {
    return '';
  }
  return formattedDate.format(format);
};

export const toSafeArray = <T>(value: unknown): T[] => {
  const result = Array.isArray(value) ? value : [value];
  return result.filter((v) => !!v);
};

export const fetchPageWithRollback = async <T extends { query: { page: number } }, Res>({
  trigger,
  params,
  onOverLimit,
}: {
  trigger: (params: T) => Promise<Res extends { data: { meta: Meta } } ? Res : never>;
  params: T;
  onOverLimit?: (pageIndex: number) => void;
}): Promise<Res | null> => {
  const res = await trigger(params);

  if (!res || !res?.data) {
    return null;
  }
  if (!res.data?.meta) {
    return res;
  }

  const { page, pageCount } = res.data.meta;
  const isValidPage = page <= pageCount || pageCount === 0;
  if (isValidPage) {
    return res;
  }
  const lastPageIndex = pageCount - 1;
  const validRes = await trigger({ ...params, query: { ...params.query, page: lastPageIndex + 1 } } as T);
  if (onOverLimit) {
    onOverLimit(lastPageIndex);
  }
  return validRes;
};

interface BackToPreviousPage {
  router: AppRouterInstance;
  rollbackUrl: string;
  forceBackToRollbackUrl?: boolean;
}
export function backToPreviousPage({ router, rollbackUrl, forceBackToRollbackUrl }: BackToPreviousPage) {
  if (forceBackToRollbackUrl) {
    router.push(rollbackUrl);
    return;
  }
  if (window.history?.length && window.history.length > 1) {
    router.back();
  } else {
    router.push(rollbackUrl);
  }
}

export const withoutLineBreak = (text: string) => text.replace('\n', '');

export const getJsonFromLocalStorage = <T>(key: string) => {
  try {
    return JSON.parse(localStorage.getItem(key) || '') as T;
  } catch (err) {
    return null;
  }
};

export const getJsonFromSessionStorage = <T>(key: string) => {
  if (isServer) {
    return null;
  }
  try {
    return JSON.parse(sessionStorage.getItem(key) || '') as T;
  } catch (err) {
    return null;
  }
};

export const setJsonToSessionStorage = <T>(key: string, value: T) => {
  if (isServer) {
    return;
  }
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.error('Failed to set sessionStorage:', err);
  }
};

// compare two arrays for equality, ignoring the order of elements
export const isArrayContentsEqual = (x: unknown, y: unknown) => {
  if (!Array.isArray(x) || !Array.isArray(y)) {
    return false;
  }
  return isEmpty(xorWith(x, y, isEqual));
};

export const translateCategory = (
  t: NextTFunction,
  category: EmissionSourceCategory | EmissionSourceSubCategory | string | unknown = '',
) => {
  const i18nKey = `org-cfv-create-emission.CAT_${String(category).replace('.', '_')}`;
  if (t.has(i18nKey)) {
    return t(i18nKey);
  }
  return category as string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mergeNestedSchemas = <T extends Record<string, any>[]>(
  ...schemas: { [K in keyof T]: yup.ObjectSchema<T[K]> }
): yup.ObjectSchema<T[number]> => {
  if (schemas.length === 0) {
    return yup.object().shape({}) as yup.ObjectSchema<T[number]>;
  }

  if (schemas.length === 1) {
    return schemas[0] as yup.ObjectSchema<T[number]>;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mergedFields: Record<string, any> = {};

  schemas.forEach((schema) => {
    Object.keys(schema.fields).forEach((key) => {
      const currentField = schema.fields[key];
      const existingField = mergedFields[key];

      if (existingField) {
        // if field already exists, merge
        if (existingField instanceof yup.ObjectSchema && currentField instanceof yup.ObjectSchema) {
          // if both are object schemas, merge them recursively
          mergedFields[key] = mergeNestedSchemas(existingField, currentField);
        } else {
          // Otherwise, use the field from the last schema (overwrite)
          mergedFields[key] = currentField;
        }
      } else {
        // If the field does not exist, add it directly
        mergedFields[key] = currentField;
      }
    });
  });

  return yup.object().shape(mergedFields) as yup.ObjectSchema<T[number]>;
};

export const downloadDivAsImage = async (
  element: React.MutableRefObject<HTMLDivElement | null>,
  name: string = 'image-from-carbonM',
) => {
  if (!element?.current) {
    return;
  }

  const clonedElement = element.current.cloneNode(true) as HTMLElement;

  const fixLayoutRecursively = (el: HTMLElement) => {
    const style = window.getComputedStyle(el);

    if (style.overflow.includes('hidden')) {
      el.style.overflow = 'visible';
      el.style.overflowX = 'visible';
      el.style.overflowY = 'visible';
    }

    if (el.offsetWidth > 0 && el.offsetHeight > 0) {
      el.style.width = `${el.offsetWidth}px`;
      el.style.height = `${el.offsetHeight}px`;
    }

    Array.from(el.children).forEach((child) => {
      if (child instanceof HTMLElement) {
        fixLayoutRecursively(child);
      }
    });
  };

  fixLayoutRecursively(clonedElement);

  const container = document.createElement('div');
  Object.assign(container.style, {
    display: 'inline-block',
    backgroundColor: 'white',
    padding: '16px',
    minWidth: `${element.current.offsetWidth}px`,
    minHeight: `${element.current.offsetHeight}px`,
  });

  container.appendChild(clonedElement);
  document.body.appendChild(container);

  try {
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const width = Math.max(container.scrollWidth, container.offsetWidth);
    const height = Math.max(container.scrollHeight, container.offsetHeight);

    const dataUrl = await toJpeg(container, {
      cacheBust: true,
      backgroundColor: 'white',
      width,
      height,
    });

    download(dataUrl, name);
  } finally {
    document.body.removeChild(container);
  }
};
